import React from "react";

/**
 * <textarea> but automatically grows in height as the user types.
 */
export default function AutoExpandingTextarea(props: any) {
  const textareaRef = React.useRef<null | HTMLTextAreaElement>(null);
  const { value, ...rest } = props;

  const updateHeight = React.useCallback(() => {
    const textareaEl = textareaRef.current;
    if (textareaEl !== null) {
      // Temporarily shrink the textarea to get the actual scroll height.
      textareaEl.style.height = "auto";
      // Set the height to the scroll height, effectively resizing the textarea.
      const newHeight = textareaEl.scrollHeight + 1; // Add 1 to prevent rounding errors resulting in a scrollbar.
      textareaEl.style.height = newHeight + "px";
      // Turn on Y scrollbar if over max-height
      const maxHeightString = textareaEl.style.maxHeight;
      if (maxHeightString !== "") {
        const maxHeight = parseFloat(maxHeightString.slice(0, -2));
        if (newHeight >= maxHeight) {
          textareaEl.style.overflowY = "auto";
        } else {
          textareaEl.style.overflowY = "hidden";
        }
      }
    }
  }, []);

  // Update textarea height whenever the value changes.
  React.useEffect(() => {
    updateHeight();
  }, [value, updateHeight]);

  // When a file is uploaded on NewChat, the draft text is carried over to Chat via
  // router state. The browser places the cursor at the start of the pre-filled value
  // when autoFocus fires, so we move it to the end on mount.
  // useEffect (not onFocus) so this only runs once, onFocus would also fire on
  // keyboard re-focus (e.g. Shift+Tab), jumping the cursor away from where the user left it.
  React.useEffect(() => {
    const el = textareaRef.current;
    if (el && el.value.length > 0) {
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, []);

  return (
    <textarea ref={textareaRef} value={value} {...rest} autoFocus></textarea>
  );
}
